(() => {
  if (window.FumidarQuantityValidation) return;
  window.FumidarQuantityValidation = true;

  const readJSON = (scope, selector, fallback) => {
    try { return JSON.parse(scope.querySelector(selector)?.textContent || 'null') ?? fallback; }
    catch { return fallback; }
  };
  const number = (value) => value === null || value === undefined || value === '' ? null : Number(value);
  const limit = (rules) => {
    const stock = rules.inventoryManagement && rules.inventoryPolicy === 'deny' ? number(rules.inventoryQuantity) : null;
    const maximum = number(rules.max);
    return { stock: Number.isFinite(stock) ? Math.max(0, stock) : null, maximum: Number.isFinite(maximum) ? maximum : null };
  };
  const problem = (quantity, rules, total = quantity, allowZero = true) => {
    if (!Number.isSafeInteger(quantity) || quantity < 0 || (!allowZero && quantity === 0)) return ['integer'];
    if (quantity === 0 && allowZero) return null;
    const bounds = limit(rules);
    if (bounds.stock !== null && total > bounds.stock) return ['stock', bounds.stock];
    if (rules.available === false) return ['unavailable'];
    if (bounds.maximum !== null && total > bounds.maximum) return ['maximum', bounds.maximum];
    const minimum = number(rules.min) || 1;
    const increment = number(rules.increment) || 1;
    if (total < minimum) return ['minimum', minimum];
    if (total % increment !== 0) return ['increment', increment];
    return null;
  };
  const userError = (message) => Object.assign(new Error(), { userMessage: message });
  const request = async (url, options = {}, format = 'json') => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const body = await response[format]();
      return { ok: response.ok, status: response.status, body };
    }
    finally { window.clearTimeout(timer); }
  };
  const apiURL = (path) => `${window.Shopify?.routes?.root || '/'}cart/${path}.js`;
  const post = async (path, body, fallback) => {
    const response = await request(apiURL(path), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const result = response.body;
    if (!response.ok || result.status >= 400) {
      const detail = result.description || result.message;
      throw userError(typeof detail === 'string' ? detail : fallback);
    }
    return result;
  };
  const freshSection = async (section, parameter, selector) => {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', section.dataset[parameter]);
    url.searchParams.set('_quantity_check', String(Date.now()));
    const response = await request(url, {}, 'text');
    if (!response.ok) throw new Error();
    const doc = new DOMParser().parseFromString(response.body, 'text/html');
    const fresh = doc.querySelector(selector);
    if (!fresh) throw new Error();
    return fresh;
  };
  const feedback = (form) => {
    const messages = readJSON(form, '[data-quantity-messages]', {});
    const region = form.querySelector('[data-quantity-error]');
    const clear = () => {
      region.hidden = true;
      region.textContent = '';
      form.querySelectorAll('[aria-invalid="true"]').forEach((input) => input.removeAttribute('aria-invalid'));
    };
    const show = (message, input, focus = true) => {
      region.textContent = message;
      region.hidden = false;
      if (input) input.setAttribute('aria-invalid', 'true');
      if (focus) window.requestAnimationFrame(() => { if (region.isConnected) (input || region).focus(); });
    };
    const format = (issue) => (messages[issue[0]] || messages.error).replace('[quantity]', String(issue[1] ?? ''));
    return { messages, clear, show, format };
  };
  const busy = (form, state) => {
    form.setAttribute('aria-busy', String(state));
    form.querySelectorAll('button, input, textarea, select').forEach((control) => {
      if (state) { control.dataset.quantityWasDisabled = String(control.disabled); control.disabled = true; }
      else if (control.dataset.quantityWasDisabled !== undefined) {
        control.disabled = control.dataset.quantityWasDisabled === 'true';
        delete control.dataset.quantityWasDisabled;
        control.removeAttribute('aria-busy');
      }
    });
  };
  const initializeCart = (section) => {
    const form = section.querySelector('[data-quantity-form]');
    if (!form || form.dataset.quantityInitialized) return;
    form.dataset.quantityInitialized = 'true';
    form.noValidate = true;
    const ui = feedback(form);
    const inputs = [...form.querySelectorAll('[data-cart-key]')];
    const lines = readJSON(section, '[data-cart-lines]', []);
    let pending = false;
    let navigating = false;
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) { navigating = false; pending = false; busy(form, false); }
    });
    const validate = (currentLines, focus = true) => {
      ui.clear();
      const quantities = new Map(inputs.map((input) => [input.dataset.cartKey, input.value.trim() === '' ? NaN : Number(input.value)]));
      const totals = new Map();
      currentLines.forEach((line) => totals.set(line.variantId, (totals.get(line.variantId) || 0) + quantities.get(line.key)));
      for (const input of inputs) {
        const line = currentLines.find((item) => item.key === input.dataset.cartKey);
        if (!line) { ui.show(ui.messages.changed, null, focus); return false; }
        const issue = problem(quantities.get(line.key), line.rules, totals.get(line.variantId));
        if (issue) { ui.show(`${line.title}: ${ui.format(issue)}`, input, focus); return false; }
      }
      return true;
    };
    inputs.forEach((input) => input.addEventListener('input', () => { if (!pending) validate(lines, false); }));
    const refresh = async (message) => {
      const fresh = await freshSection(section, 'cartSection', '[data-cart-section]');
      const note = form.elements.note?.value;
      section.replaceWith(fresh);
      initializeCart(fresh);
      const newForm = fresh.querySelector('[data-quantity-form]');
      if (newForm) {
        if (typeof note === 'string' && newForm.elements.note) newForm.elements.note.value = note;
        feedback(newForm).show(message);
      } else {
        const region = document.createElement('p');
        region.className = 'form-message form-message--error';
        region.setAttribute('role', 'alert');
        region.tabIndex = -1;
        region.textContent = message;
        fresh.querySelector('.page-width').prepend(region);
        region.focus();
      }
    };
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (pending) return;
      ui.clear();
      const invalid = inputs.find((input) => input.value.trim() === '' || !Number.isSafeInteger(Number(input.value)) || Number(input.value) < 0);
      if (invalid) { ui.show(ui.messages.integer, invalid); return; }
      const checkout = event.submitter?.name === 'checkout';
      const desired = new Map(inputs.map((input) => [input.dataset.cartKey, Number(input.value)]));
      const note = form.elements.note?.value;
      const noteChanged = form.elements.note && note !== form.elements.note.defaultValue;
      pending = true;
      busy(form, true);
      try {
        const fresh = await freshSection(section, 'cartSection', '[data-cart-section]');
        const currentLines = readJSON(fresh, '[data-cart-lines]', []);
        if (lines.length !== currentLines.length || lines.some((line) => !currentLines.some((current) => current.key === line.key && current.quantity === line.quantity))) {
          await refresh(ui.messages.changed);
          return;
        }
        if (!validate(currentLines)) return;
        const changes = currentLines.filter((line) => desired.get(line.key) !== line.quantity)
          .sort((a, b) => (desired.get(a.key) - a.quantity) - (desired.get(b.key) - b.quantity));
        let cart;
        for (const line of changes) {
          if (cart && !cart.items.some((item) => item.key === line.key)) throw userError(ui.messages.changed);
          cart = await post('change', { id: line.key, quantity: desired.get(line.key) }, ui.messages.error);
        }
        if (noteChanged) await post('update', { note }, ui.messages.error);
        const updated = await freshSection(section, 'cartSection', '[data-cart-section]');
        const updatedLines = readJSON(updated, '[data-cart-lines]', []);
        const expected = new Map();
        currentLines.forEach((line) => expected.set(line.variantId, (expected.get(line.variantId) || 0) + desired.get(line.key)));
        const actual = new Map();
        updatedLines.forEach((line) => actual.set(line.variantId, (actual.get(line.variantId) || 0) + line.quantity));
        if ([...new Set([...actual.keys(), ...expected.keys()])].some((id) => (actual.get(id) || 0) !== (expected.get(id) || 0))) throw userError(ui.messages.changed);
        for (const line of updatedLines) {
          const issue = problem(line.quantity, line.rules, actual.get(line.variantId));
          if (issue) throw userError(`${line.title}: ${ui.format(issue)}`);
        }
        if (checkout && updatedLines.length) {
          const checkoutForm = document.createElement('form');
          checkoutForm.method = 'post';
          checkoutForm.action = form.action;
          const field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'checkout';
          field.value = 'checkout';
          checkoutForm.append(field);
          document.body.append(checkoutForm);
          navigating = true;
          HTMLFormElement.prototype.submit.call(checkoutForm);
        } else { navigating = true; window.location.reload(); }
      } catch (error) {
        const message = error.userMessage || ui.messages.error;
        try { await refresh(message); } catch { ui.show(message); }
      } finally {
        if (!navigating) { pending = false; busy(form, false); }
      }
    });
    validate(lines, false);
  };
  const initializeProduct = (section) => {
    const form = section.querySelector('.main-product__form');
    const input = form?.elements.quantity;
    if (!input || form.dataset.quantityInitialized || !form.querySelector('[data-quantity-error]')) return;
    form.dataset.quantityInitialized = 'true';
    form.noValidate = true;
    const ui = feedback(form);
    let variants = readJSON(section, '[data-product-variants]', []);
    let pending = false;
    let navigating = false;
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) { navigating = false; pending = false; busy(form, false); }
    });
    const selected = () => variants.find((variant) => String(variant.id) === form.elements.id?.value);
    const updateBounds = () => {
      ui.clear();
      const rules = selected()?.quantityRules;
      if (!rules) { input.removeAttribute('max'); return; }
      const bounds = limit(rules);
      const caps = [bounds.stock, bounds.maximum].filter((cap) => cap !== null);
      input.min = '1';
      input.step = '1';
      if (caps.length) input.max = String(Math.min(...caps)); else input.removeAttribute('max');
    };
    section.addEventListener('click', (event) => {
      if (event.target.closest('[data-option-value]')) queueMicrotask(updateBounds);
    });
    input.addEventListener('input', () => {
      if (pending) return;
      ui.clear();
      const rules = selected()?.quantityRules;
      const issue = rules && problem(input.value.trim() === '' ? NaN : Number(input.value), rules, Number(input.value), false);
      if (issue && !['minimum', 'increment'].includes(issue[0])) ui.show(ui.format(issue), input, false);
    });
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (pending) return;
      ui.clear();
      const variant = selected();
      const quantity = input.value.trim() === '' ? NaN : Number(input.value);
      if (!variant) { ui.show(ui.messages.unavailable, input); return; }
      const issue = problem(quantity, variant.quantityRules, quantity, false);
      if (issue && issue[0] === 'integer') { ui.show(ui.format(issue), input); return; }
      const data = new FormData(form);
      pending = true;
      busy(form, true);
      try {
        const [fresh, cartResponse] = await Promise.all([
          freshSection(section, 'productSection', '[data-product-section]'),
          request(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
        ]);
        if (!cartResponse.ok) throw new Error();
        const cart = cartResponse.body;
        variants = readJSON(fresh, '[data-product-variants]', []);
        const current = variants.find((item) => item.id === variant.id);
        if (!current) throw userError(ui.messages.unavailable);
        const inCart = cart.items.filter((item) => item.variant_id === current.id).reduce((total, item) => total + item.quantity, 0);
        const latestIssue = problem(quantity, current.quantityRules, quantity + inCart, false);
        if (latestIssue) { updateBounds(); ui.show(ui.format(latestIssue), input); return; }
        const response = await request(apiURL('add'), { method: 'POST', headers: { Accept: 'application/json' }, body: data });
        const result = response.body;
        if (!response.ok || result.status >= 400) throw userError(typeof result.description === 'string' ? result.description : ui.messages.error);
        navigating = true;
        window.location.assign(section.dataset.cartUrl);
      } catch (error) { ui.show(error.userMessage || ui.messages.error, input); }
      finally { if (!navigating) { pending = false; busy(form, false); } }
    });
    updateBounds();
  };
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-cart-section]').forEach(initializeCart);
    scope.querySelectorAll('[data-product-section]').forEach(initializeProduct);
    if (scope.matches?.('[data-cart-section]')) initializeCart(scope);
    if (scope.matches?.('[data-product-section]')) initializeProduct(scope);
  };
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('pageshow', () => initialize());
  initialize();
})();
